import React, { useCallback, useMemo } from 'react';

import { DashIcon, PlusIcon, XIcon } from '@primer/octicons-react';
import { Button as PrimerButton, StyledOcticon } from '@primer/react';

import classNames from 'classnames';
import isFunction from 'lodash/isFunction';

type ButtonArgTypes = {
  className?: string,
  children?: React.ReactNode,
  onClick?: React.MouseEventHandler<HTMLButtonElement> | undefined,
  textColor?: string,
  hoverTextColor?: string,
  activeTextColor?: string,
  border?: boolean,
  sx?: any,
  size?: string,
  variant?: string,
  iconSize?: number,
  disabled?: boolean
}

export const BUTTON_VARIANT = {
  DEFAULT: 'default',
  PRIMARY: 'primary',
  INVISIBLE: 'invisible',
  DANGER: 'danger',
  CLOSE: 'close',
  ADD: 'add',
  REMOVE: 'remove',
}

const AddButton: React.FC<ButtonArgTypes> = ({ 
  className = '',
  onClick = () => {},
  textColor = 'border-dark',
  hoverTextColor = 'border-darker',
  activeTextColor = 'info',
  border = true,
  ...rest 
}) => (
  <button
    className={
      classNames(
        'flex justify-center items-center p-1 h-8 w-8',
        `border-${textColor} text-${textColor} hover:text-${hoverTextColor} hover:border-${hoverTextColor} active:${activeTextColor} active:border-${activeTextColor}`,
        { 'rounded border': border },
        className
      )
    }
    type="button"
    aria-label="add"
    onClick={onClick}
    {...rest}
  >
    <StyledOcticon icon={PlusIcon} size="small" />
  </button>
)

const RemoveButton: React.FC<ButtonArgTypes> = ({
  className = '',
  onClick = () => {},
  textColor = 'border-dark',
  hoverTextColor = 'border-darker',
  activeTextColor = 'info',
  border = true,
  ...rest
}) => (
  <button
    className={
      classNames(
        'flex justify-center items-center p-1 h-8 w-8',
        `border-${textColor} text-${textColor} hover:text-${hoverTextColor} hover:border-${hoverTextColor} active:${activeTextColor} active:border-${activeTextColor}`,
        { 'rounded border': border },
        className
      )
    }
    type="button"
    aria-label="remove"
    onClick={onClick}
    {...rest}
  >
    <StyledOcticon icon={DashIcon} size="small" />
  </button>
)

const CloseButton: React.FC<ButtonArgTypes> = ({
  className = '',
  onClick = () => {},
  textColor = 'border-light',
  hoverTextColor = 'border-dark',
  activeTextColor = 'border-darker',
  ...rest
}) => (
  <button
    className={
      classNames(
        `h-8 w-8 text-${textColor} hover:text-${hoverTextColor} active:text-${activeTextColor}`,
        className,
      )
    }
    type="button"
    aria-label="Close"
    onClick={onClick}
    {...rest}
  >
    <StyledOcticon icon={XIcon} size="small" />
  </button>
);

const getButtonComponent = (variant = BUTTON_VARIANT.DEFAULT) => {
  switch (variant) {
  case BUTTON_VARIANT.CLOSE:
    return CloseButton;
  case BUTTON_VARIANT.ADD:
    return AddButton;
  case BUTTON_VARIANT.REMOVE:
    return RemoveButton;
  default:
    return PrimerButton;
  }
}

export const Button: React.FC<ButtonArgTypes> = ({
  children = [],
  onClick = () => {},
  size = 'medium',
  variant = BUTTON_VARIANT.DEFAULT,
  ...rest
}) => {

  const handleClick = useCallback((e: React.MouseEvent) => {
    if (isFunction(onClick)) {
      onClick(e);
    }
  }, [onClick])

  const ButtonComponent = useMemo(() => getButtonComponent(variant), [variant]);

  return (
    <ButtonComponent
      variant={variant}
      size={size}
      onClick={(e: React.MouseEvent) => handleClick(e)}
      {...rest}
    >
      { children }
    </ButtonComponent>
  )
}

export default Button;
